<script lang="ts" setup>
	import type { DateFilterParameter } from '~/components/Forms/date-filter.vue'
	import usePurchaseApi from '~/services/apis/purchases-info'
	import { useCustomDate } from '~/composables/useCustomDate'
	import type { IRequestResponse, PaginationContext, PolicyResponse, RecordSchema } from '@/types'

	const { data: historyInformation, planId } = defineProps<{
		data: PlanInformationActivities | null
		planId: string
		loadingRecords?: boolean
		exporting?: boolean
	}>()
	const { formatDate, convertMonthToNumber } = useCustomDate()
	const { format, currencyToNaira } = useCurrency()
	const { toLower } = useUtils()
	const showDialog = shallowRef(false)
	const exportingRecords = shallowRef(false)
	const loadingTableRecords = shallowRef(false)
	const totalRecords = shallowRef('0')
	const currentFocus = shallowRef<'policies' | 'deactivate' | ''>('')
	const currentFilter = shallowRef<{ year: string; month: string }>({
		year: '',
		month: ''
	})
	const searchVal = shallowRef('')

	const columns = computed<TableColumn[]>(() => [
		{
			title: 'S/N',
			dataIndex: 'sn',
			key: 'sn',
			width: 50
		},
		{
			title: 'Staff Name',
			dataIndex: 'first_name',
			key: 'staff_name'
		},
		{
			title: 'Email Address',
			dataIndex: 'email',
			key: 'email'
		},
		{
			title: 'Phone Number',
			dataIndex: 'phone_number',
			key: 'phone_number'
		}
	])
	const tableOptions = computed<TableFilterOptions>(() => ({
		pagination: true,
		filter: {
			search: searchVal.value,
			...currentFilter.value
		}
	}))
	const { fetchDeactivatePolicies, filterPolicies } = usePurchaseApi()
	const fetchRecords = async (arg: object) => {
		const opt = arg as typeof currentFilter.value & Record<string, unknown>
		if (currentFocus.value === 'deactivate') {
			const result = await fetchDeactivatePolicies<IRequestResponse<PaginationContext<PolicyResponse[]>>>(
				planId,
				opt
			)
			if (result?.data) {
				totalRecords.value = format(Number(result.data.total_count))
			}
			return result?.data ?? null
		}
		const result = await filterPolicies<IRequestResponse<PaginationContext<PolicyResponse[]>>>(planId, opt)
		if (result?.data) {
			totalRecords.value = format(Number(result.data.total_count))
		}
		return result?.data ?? null
	}
	const openDialog = (key: typeof currentFocus.value, opt: typeof currentFilter.value) => {
		currentFocus.value = key
		totalRecords.value = '0'
		showDialog.value = true
		currentFilter.value = opt
	}

	const events = (event: PlanInformationActivities['details'][number]['months'][number]['events'][number]) => {
		type K =
			| 'Next premium'
			| 'Deactivated policies'
			| 'Deactivation type'
			| 'Added policies'
			| 'Premium Paid'
			| 'Renewal Date'
			| 'Cover Period'
			| 'Change Type'
			| 'Number of slots'
		type ArrangeType = Record<
			K,
			{
				value: string
				count?: string
				className: string
				valueClass?: string
				disabled: boolean
				change?: () => void
			}
		>
		const year = formatDate(event.timestamp, 'YYYY')
		const month = formatDate(event.timestamp, 'MM')
		const opt = {
			year,
			month
		}

		const result: ArrangeType = {
			'Next premium': {
				value: currencyToNaira(event?.next_premium || 0),
				className: 'text-primary-2',
				disabled: !event?.next_premium
			},
			'Number of slots': {
				value: format(event?.total_slots ?? 0) || '',
				className: '',
				valueClass: 'font-semibold',
				disabled: !event?.total_slots
			},
			'Deactivated policies': {
				value: format(event?.staff_policies_deactivated ?? 0) || '',
				className: '',
				valueClass: 'font-semibold',
				disabled: !event?.staff_policies_deactivated,
				change: () => openDialog('deactivate', opt)
			},
			'Deactivation type': {
				value: event?.is_immediate ? 'Instant' : 'Renewal',
				className: '',
				disabled: event?.is_immediate === undefined
			},
			'Added policies': {
				value: format(event?.staff_added_count ?? 0) || '--',
				className: '',
				valueClass: 'font-semibold',
				disabled: !event?.staff_added_count
			},
			'Premium Paid': {
				value: currencyToNaira(event?.amount_paid || 0),
				className: 'text-primary-2',
				disabled: !event?.amount_paid
			},
			'Renewal Date': {
				value: formatDate(event?.expiry_date || '', 'Do MMMM'),
				className: '',
				disabled: !event?.expiry_date
			},
			'Cover Period': {
				value: event?.cover_period || '',
				className: '',
				disabled: !event?.cover_period
			},
			'Change Type': {
				value: '',
				className: '',
				disabled: true
			}
		}
		return result
	}

	const exportRecords = () => {
		const { readFile, downloadFileResource } = useFileExplorer()
		resolve(async () => {
			let response: File | null = null
			if (currentFocus.value === 'deactivate') {
				response = await fetchDeactivatePolicies<File>(planId, {
					...currentFilter.value,
					is_export: true
				})
			} else if (currentFocus.value === 'policies') {
				response = await filterPolicies<File>(planId, {
					...currentFilter.value,
					is_export: true
				})
			}
			if (response && response?.size) {
				readFile(response as File, 'readAsDataURL', e => {
					const label = currentFocus.value === 'deactivate' ? 'deactivated-policies' : 'policies'
					downloadFileResource(
						(e?.target as FileReader)?.result as string,
						`${label}-${currentFilter.value.month}-${currentFilter.value.year}.xlsx`
					)
				})
			} else {
				useNotification().alert({
					status: 400,
					message: 'Records not available for export'
				})
			}
			return null
		}, exportingRecords)
	}
	const yearFilter = defineModel<DateFilterParameter | null>({ default: null })
	const emits = defineEmits<{
		(event: 'export'): void
	}>()
</script>

<template>
	<div>
		<div class="flex flex-wrap items-center justify-between border-b border-gray-200 pb-5">
			<div class="flex items-center gap-x-3">
				<forms-date-filter v-model:value="yearFilter" />
			</div>
			<shared-export-button :loading="exporting" @click="emits('export')" />
		</div>

		<div class="my-8 flex gap-x-2 gap-y-3 text-sm font-normal tracking-wider text-gray-600">
			<div
				class="inline-flex h-[20px] w-[20px] flex-none items-center justify-center rounded-full border border-gray-400"
			>
				<lazy-tools-icon
					icon="employee"
					dir="fill"
					path-class="opacity-100 fill-gray-500"
					:height="12"
					:width="12"
				/>
			</div>
			<span>
				Total Staff:
				<span class="font-medium">{{ format(historyInformation?.total_staff_policies_count || 0) }}</span>
			</span>
		</div>

		<loader-skeleton v-if="loadingRecords" :count="1" class-list="w-full bg-purple-25" :max-width="false" />

		<div
			v-for="(history, index) in historyInformation?.details || []"
			v-else
			:key="index"
			class="max-h-[1000px] w-full overflow-y-auto bg-purple-25 px-3 py-5"
		>
			<template v-for="(item, mIndex) in history.months" :key="mIndex">
				<template v-if="item.events.length">
					<div class="mb-[18px] flex items-center justify-between">
						<h3 class="border-l-2 border-purple-500 px-3 text-sm font-semibold text-gray-700">
							{{ item?.label || '' }}, {{ history?.year || '--' }}
						</h3>

						<div class="flex items-center gap-x-2">
							<span class="text-sm font-medium text-gray-600">
								Total Staff:
								<span class="font-semibold">{{ format(item?.added_staff_count || 0) }}</span>
							</span>
							<button
								v-if="Boolean(item?.added_staff_count)"
								type="button"
								:data-testid="`view-button-${index}`"
								class="flexed justify-center gap-[10px] rounded-[4px] bg-primary-2 p-[1px_4px] text-xs font-medium text-white"
								@click="
									openDialog('policies', {
										year: history?.year || '',
										month: convertMonthToNumber(item?.label || '')
									})
								"
							>
								View
							</button>
						</div>
					</div>

					<ul class="mb-2" :class="{ hidden: !item.events.length }">
						<li
							v-for="(event, eIndex) in item.events"
							:key="`event-${eIndex}`"
							class="group space-y-[8px] border-b-[2px] border-dotted pb-3 tracking-wide"
						>
							<div class="flex flex-wrap items-center justify-between gap-3 pt-[18px] group-first:pt-0">
								<div class="inline-flex items-center gap-x-[4px]">
									<span
										class="text-sm font-medium"
										:class="{
											'text-red-500': toLower(event.type).includes('expired'),
											'text-gray-700': !toLower(event.type).includes('expired')
										}"
									>
										{{ event.type }}
									</span>
									<div
										v-show="event.staff_added_count || event.staff_policies_deactivated"
										class="inline-flex h-[14px] w-[18px] items-center justify-center rounded-[3px] px-[4px] py-[2px] text-[10px] font-medium text-white"
										:class="{
											'bg-warning-500': event.staff_policies_deactivated,
											'bg-purple-500': event.staff_added_count
										}"
									>
										{{ format(event.staff_added_count || event.staff_policies_deactivated || 0) }}
									</div>
								</div>

								<div
									v-if="event.timestamp"
									class="inline-flex items-center gap-x-[6px] text-xs text-gray-600"
								>
									{{ formatDate(event.timestamp, 'Do MMMM') }}
									<div class="h-[4px] w-[4px] rounded-full bg-gray-200" />
									<span class="h">{{ formatDate(event.timestamp, 'h:mm a') }}</span>
								</div>
							</div>

							<template v-for="(val, k, sIndex) in events(event)" :key="`stats-${sIndex}`">
								<div
									v-if="!val.disabled"
									:class="val.className"
									class="flex flex-wrap items-center justify-between gap-3"
								>
									<div class="inline-flex gap-x-[8px] text-sm text-gray-600">
										<span class="font-bold">-</span>
										<span class="font-normal">{{ k }}</span>
									</div>
									<div class="inline-flex gap-x-3 text-xs font-medium capitalize">
										<span :class="val.valueClass">{{ val.value }}</span>
										<button
											v-if="val?.change"
											type="button"
											class="text-primary-2 underline"
											@click="val?.change()"
										>
											View
										</button>
									</div>
								</div>
							</template>
						</li>
					</ul>
				</template>
			</template>
		</div>

		<dialogs
			v-model:show="showDialog"
			close-on-bg
			:show-close-button="false"
			:ui="{ modal: 'max-w-[628px] w-full px-[32px]' }"
		>
			<header class="flexed mt-10 items-center justify-center">
				<div class="flex items-center gap-x-[12px]">
					<span class="text-sm font-medium text-gray-600 md:text-2xl">
						{{ currentFocus === 'deactivate' ? 'Deactivated staff' : 'View Total Staff' }}
					</span>
					<div class="flexed justify-center rounded-[20px] bg-purple-500 p-[4px_18px] text-white">
						{{ totalRecords }}
					</div>
				</div>
				<div
					class="flexed mt-[18px] justify-center rounded-lg bg-purple-50 p-[8px_20px] font-medium capitalize text-purple-500"
				>
					{{ convertMonthToNumber(currentFilter.month, true) }} {{ currentFilter.year }}
				</div>
			</header>

			<div class="mt-[28px] flex flex-wrap items-stretch gap-4 gap-x-[12px]">
				<forms-search-field v-model:value="searchVal" class="flex-1" />

				<shared-export-button :loading="exportingRecords" @click="exportRecords" />
			</div>

			<div class="mt-[24px] border-x border-t px-[6px] pt-[12px]">
				<ui-data-table
					:columns="columns"
					:records="fetchRecords"
					:loading="loadingTableRecords"
					:options="tableOptions"
				>
					<template #bodyCell="{ column, record }">
						<span v-if="column.key === 'staff_name'"> {{ record.first_name }} {{ record.last_name }} </span>
					</template>
				</ui-data-table>
			</div>
		</dialogs>
	</div>
</template>
